import { useMemo, useState } from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import delay from 'lodash/delay';
import isEmpty from 'lodash/isEmpty';

import DialogTitleWithX from '@/components/DialogTitleWithX';
import PlusIcon from '@/components/Icons/PlusIcon';
import SearchIcon from '@/components/Icons/SearchIcon';
import { OrganizationRole, TeamRoleEnum, UserRoleInput } from '@/graphql';
import useUser from '@/hooks/useUser';
import { useInviteUserMutation } from '@/sections/Members/__generated__/mutations.generated';
import { useFilterOrgUsers } from '@/sections/Members/hooks';
import RolesMenu from '@/sections/Teams/RolesMenu';
import { getBorder } from '@/theme/utils';
import { EMAIL_REGEX } from '@/utils/email';
import { DialogProps } from '@toolpad/core/useDialogs/useDialogs';
import { useSnackbar } from 'notistack';

import TeamName from '../TeamName';
import { useSelectedTeamId, useTeam } from '../hooks';
import { useAddOrUpdateUserTeamMemberRole } from './hooks';

const DIALOG_WIDTH = 500;

function AddMember({ open, onClose }: DialogProps) {
  const user = useUser();
  const { selectedTeamId } = useSelectedTeamId();
  const { team } = useTeam(selectedTeamId);
  const { addOrUpdateUserTeamMemberRole, loading } = useAddOrUpdateUserTeamMemberRole();
  const { enqueueSnackbar } = useSnackbar();
  const [userRoles, setUserRoles] = useState<UserRoleInput[]>([]);
  const [query, setQuery] = useState('');
  const { filteredUsers, allUsers } = useFilterOrgUsers(query);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [inviteUser, { loading: inviteUserLoading }] = useInviteUserMutation();

  const membersToShow = useMemo(() => {
    return filteredUsers.filter(
      (user) =>
        // exclude all users that are already in the userRoles
        !userRoles.some((userRole) => userRole.username === user.username),
    );
  }, [filteredUsers, userRoles]);

  async function addMember() {
    try {
      const { data } = await addOrUpdateUserTeamMemberRole({
        variables: {
          userRoles: userRoles.filter((userRole) => userRole.username !== ''),
          teamId: selectedTeamId,
        },
      });

      if (data?.addOrUpdateUserTeamMemberRole.ok) {
        enqueueSnackbar('Member added successfully', { variant: 'success' });
        onClose();
      } else {
        enqueueSnackbar('Failed to add member', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }

  async function handleInviteNewMember() {
    try {
      const {
        data: {
          inviteUser: { error },
        },
      } = await inviteUser({
        variables: {
          email: query.trim(),
          roleName: OrganizationRole.Creator,
          invitedBy: user.username,
        },
      });

      if (error) {
        enqueueSnackbar(error, { variant: 'error' });
      } else {
        setAnchorEl(null);
        enqueueSnackbar(`Invite sent to ${query}`, { variant: 'success' });
        delay(() => setQuery(''), 200);
      }
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  }

  function getFilteredUsers() {
    if (isEmpty(membersToShow)) {
      const isEmail = EMAIL_REGEX.test(query);
      const isDisabled = !isEmail || inviteUserLoading;

      return (
        <>
          <Stack alignItems="center" justifyContent="center" height={100}>
            <Typography variant="h5">No members in the organization</Typography>
          </Stack>
          <Tooltip
            title={isEmail ? `Invite ${query}` : 'Type an email address to invite a new member'}
          >
            <Stack
              sx={{
                backgroundColor: isDisabled ? 'background.subtle' : 'transparent',
                cursor: isDisabled ? 'initial' : 'pointer',
                '&:hover': isDisabled
                  ? {}
                  : {
                      backgroundColor: (theme) => theme.palette.background.subtle,
                    },
                borderTop: (theme) => getBorder(theme.palette.border.default),
                height: 48,
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'row',
                margin: (theme) => theme.spacing(-1),
                gap: (theme) => theme.spacing(1),
              }}
              onClick={() => !isDisabled && handleInviteNewMember()}
            >
              <Stack direction="row" alignItems="center" justifyContent="center">
                <Stack width={18} justifyContent="center" alignItems="center">
                  {inviteUserLoading ? (
                    <CircularProgress size={14} sx={{ color: 'text.subtle' }} />
                  ) : (
                    <PlusIcon sx={{ color: 'text.subtle' }} />
                  )}
                </Stack>
              </Stack>
              <Typography
                variant="h5"
                component="span"
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  color: isDisabled ? 'text.disabled' : 'text.default',
                }}
              >
                Invite
                <Typography
                  variant="h5"
                  component="span"
                  sx={{
                    maxWidth: 90,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                  }}
                >
                  {query}
                </Typography>
                to the organization and this team
              </Typography>
            </Stack>
          </Tooltip>
        </>
      );
    }

    return membersToShow.map((user) => {
      const isAlreadyInTeam =
        !!team.members.some((member) => member.user.id === user.id) ||
        user.roleName === OrganizationRole.Admin;

      return (
        <MenuItem
          key={user.id}
          disabled={isAlreadyInTeam}
          onClick={() =>
            setUserRoles([
              ...userRoles,
              { username: user.username, role_name: TeamRoleEnum.TeamMember },
            ])
          }
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1}>
            <Typography
              variant="h5"
              component="span"
              sx={{
                maxWidth: isAlreadyInTeam ? 200 : 350,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user.name}{' '}
              <Typography variant="body1" color="text.muted" component="span">
                {user.email}
              </Typography>
            </Typography>
            {isAlreadyInTeam && (
              <Typography variant="body1" color="text.muted">
                Already a member
              </Typography>
            )}
          </Stack>
        </MenuItem>
      );
    });
  }

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      slotProps={{
        paper: {
          sx: { width: DIALOG_WIDTH },
        },
      }}
    >
      <DialogTitleWithX
        onClose={onClose}
        title="Add members to this team"
        sx={{
          pb: 2,
          borderBottom: (theme) => getBorder(theme.palette.border.default),
          paddingBottom: (theme) => theme.spacing(2),
        }}
      >
        <TeamName
          name={team.name}
          icon={team.icon}
          maxWidth={200}
          prefix={<Typography variant="subtitle2">Add members to</Typography>}
        />
      </DialogTitleWithX>
      <DialogContent sx={{ mt: 2 }}>
        <Stack gap={2} maxHeight={400} overflow="auto">
          <Stack gap={1} flex={1}>
            <Typography variant="subtitle2">Member</Typography>
            <TextField
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find members by name or email"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Stack gap={1} flex={1}>
            {userRoles.map((userRole, index) => {
              const user = allUsers.find((user) => user.username === userRole.username);

              return (
                // eslint-disable-next-line react/no-array-index-key
                <Stack key={index} gap={1} direction="row" alignItems="center">
                  <Stack gap={1} flex={1}>
                    <Typography variant="h5">{user?.name}</Typography>
                  </Stack>
                  <RolesMenu
                    value={userRole.role_name}
                    onChange={(e) =>
                      setUserRoles(
                        userRoles.map((userRole, i) =>
                          i === index
                            ? { ...userRole, role_name: e.target.value as TeamRoleEnum }
                            : userRole,
                        ),
                      )
                    }
                  />
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={(theme) => ({
          background: theme.palette.background.subtle,
          borderTop: getBorder(theme.palette.border.default),
          justifyContent: 'space-between',
        })}
      >
        <Button variant="outlined" onClick={() => onClose()}>
          Cancel
        </Button>
        <Button
          loading={loading}
          disabled={!userRoles.some((userRole) => userRole.username !== '')}
          onClick={addMember}
          variant="contained"
          color="success"
        >
          Add member
        </Button>
      </DialogActions>
      <Popover
        disableAutoFocus
        autoFocus={false}
        disableEnforceFocus
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: DIALOG_WIDTH - 48,
              maxHeight: 300,
              overflow: 'auto',
              p: 1,
            },
          },
        }}
      >
        {getFilteredUsers()}
      </Popover>
    </Dialog>
  );
}

export default AddMember;
